// The names of the fields of a row as the user knows them, for "What
// changed" (diff.js). Pure: the titles of other rows (a variant is known by
// the title of its block) come from titleOf(accessor, id), injected by the
// caller (the components read MemoryDB, this module never does).
//
// A path is a list of segments: a field name (string), or an element of a
// keyed array: { key, element, index } (element: the element as one of the
// sides has it, index: its position there).

// The keyed arrays of a row: the top-level field of the accessor, and how
// an element is known.
const ELEMENTS = {
  blocks: {
    blocks: ({ key, element }, titleOf) =>
      `Sub-block «${
        element?.title ||
        (element?.blockId && titleOf?.("blocks", element.blockId)) ||
        key
      }»`
  },
  endpoints: {
    variants: ({ key }, titleOf) =>
      `Variant «${titleOf?.("blocks", key) || key}»`
  },
  rules: {
    rule: ({ index, element }) =>
      `Statement ${index + 1}${element?.field ? ` (${element.field})` : ""}`
  }
};

// Any field, at any depth
const COMMON = {
  title: "Title",
  description: "Description",
  active: "Active",
  scheduled: "Scheduled",
  scheduling: "Scheduling",
  schedulingMap: "Scheduling map",
  condition: "Conditions",
  results: "Results",
  reference: "Reference",
  prevReference: "Previous reference",
  layout: "Layout",
  priority: "Priority",
  realm: "Realm",
  contentType: "Content type",
  params: "Params",
  paramsId: "Params preset",
  input: "Input",
  output: "Output",
  uiParams: "UI params",
  queryId: "Query",
  type: "Type",
  blockId: "Block",
  value: "Value",
  field: "Field",
  boost: "Boost"
};

// Top-level fields of an accessor (before COMMON)
const TOP = {
  blocks: {
    blocks: "Sub-blocks",
    enabledPagesId: "Enabled pages",
    isPage: "Page",
    fullSubstituteFallback: "Full substitute fallback",
    includedMetadata: "Included metadata",
    searchMetadata: "Search metadata",
    canBeFallback: "Can be fallback"
  },
  endpoints: {
    variants: "Variants",
    blockId: "Default page",
    haveScheduledVariants: "Scheduled variants",
    enableAnalitica: "Analitica",
    paginateFromCache: "Paginate from cache"
  },
  rules: { rule: "Statements", clauses: "Clauses" },
  properties: {
    tree: "Pages",
    blocksMetadata: "Blocks metadata",
    params: "Page Default Settings › Params",
    paramsId: "Page Default Settings › Params preset",
    input: "Page Default Settings › Input",
    output: "Page Default Settings › Output",
    logOperation: "Page Default Settings › Log operation"
  }
};

// Two fields that read as one (input.decorators: "Input decorators")
const PAIRS = {
  "input.decorators": "Input decorators",
  "output.decorators": "Output decorators",
  "input.schema": "Input schema"
};
const PROPERTY_PAIRS = {
  "input.decorators": "Page Default Settings › Input decorators",
  "output.decorators": "Page Default Settings › Output decorators",
  "input.schema": "Page Default Settings › Input schema"
};

const isElement = (seg) => seg !== null && typeof seg === "object";

// "blocks[I71rzIzSb2].results.max": the path of a change (merge's `both`,
// the `path` of a line). "" for the row itself.
export function pathOf(segs = []) {
  let path = "";
  for (const seg of segs) {
    if (isElement(seg)) path += `[${seg.key}]`;
    else path += path ? `.${seg}` : seg;
  }
  return path;
}

// "Sub-block «INF Serie TV 4K» › Results › max", "Variant «Home» ›
// Conditions", "Title", "Params › cacheConfig › cacheTTL" (a field with no
// name of its own: its key). "Item" for the row itself.
export function labelOf(accessor, segs = [], { titleOf } = {}) {
  if (!segs.length) return "Item";

  const parts = [];
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    if (isElement(seg)) {
      const name = i === 1 && ELEMENTS[accessor]?.[segs[0]];
      parts[parts.length - 1] = name ? name(seg, titleOf) : `«${seg.key}»`;
      continue;
    }

    const prev = segs[i - 1];
    if (i === 1 && typeof prev === "string") {
      const pair = (accessor === "properties" ? PROPERTY_PAIRS : PAIRS)[
        `${prev}.${seg}`
      ];
      if (pair) {
        parts[parts.length - 1] = pair;
        continue;
      }
    }

    parts.push(
      (i === 0 && TOP[accessor]?.[seg]) ||
        (Object.prototype.hasOwnProperty.call(COMMON, seg) && COMMON[seg]) ||
        seg
    );
  }
  return parts.join(" › ");
}
